import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  Button,
  Checkbox,
  TextField,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const InvoiceFiltersModal = ({
  isOpen,
  toggleModalOpen,
  currentFilters,
  vendors,
  onApplyFilters,
}) => {
  const { control } = useForm();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);

  useEffect(() => {
    if (isOpen && currentFilters) {
      setStartDate(currentFilters.startDate || null);
      setEndDate(currentFilters.endDate || null);
      setSelectedVendorIds(currentFilters.vendorIds || []);
    }
  }, [isOpen, currentFilters]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    // If start date is cleared, also clear end date
    if (!date) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    // If end date is cleared, also clear start date
    if (!date) {
      setStartDate(null);
    }
  };

  const handleVendorToggle = (vendorId) => {
    if (selectedVendorIds.includes(vendorId)) {
      setSelectedVendorIds(selectedVendorIds.filter((id) => id !== vendorId));
    } else {
      setSelectedVendorIds([...selectedVendorIds, vendorId]);
    }
  };

  const handleClearFilters = () => {
    onApplyFilters({
      startDate: null,
      endDate: null,
      vendorIds: [],
    });
    closeModal();
  };

  const closeModal = () => {
    toggleModalOpen(false);
  };

  const handleSubmit = () => {
    // Validate date range - both must be set or both must be null
    let validStartDate = startDate;
    let validEndDate = endDate;

    if ((startDate && !endDate) || (!startDate && endDate)) {
      // If only one date is set, clear both
      validStartDate = null;
      validEndDate = null;
    }

    const filters = {
      startDate: validStartDate,
      endDate: validEndDate,
      vendorIds: selectedVendorIds,
    };

    onApplyFilters(filters);
    closeModal();
  };

  const hasActiveFilters =
    startDate || endDate || selectedVendorIds.length > 0;

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="invoice-filters-modal"
      aria-describedby="modal for filtering invoices"
    >
      <Box sx={modalBoxStyle}>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <Grid item>
            <Typography variant="h4">Filter Invoices</Typography>
          </Grid>
          <Grid item>
            <IconButton
              sx={{ backgroundColor: "transparent" }}
              onClick={closeModal}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Grid>
        </Grid>

        {/* Date Range Section */}
        <Box sx={{ marginBottom: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, marginBottom: 1 }}
              >
                From
              </Typography>
              <Controller
                valueName="selected"
                control={control}
                name="startDate"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      dateFormat="mm/dd/yyyy"
                      placeholder="mm/dd/yyyy"
                      value={startDate}
                      onChange={(e) => {
                        field.onChange(e);
                        handleStartDateChange(e);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          autoComplete="off"
                          sx={{
                            "& legend": { display: "none" },
                            "& fieldset": { top: 0 },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, marginBottom: 1 }}
              >
                To
              </Typography>
              <Controller
                valueName="selected"
                control={control}
                name="endDate"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      dateFormat="mm/dd/yyyy"
                      placeholder="mm/dd/yyyy"
                      value={endDate}
                      onChange={(e) => {
                        field.onChange(e);
                        handleEndDateChange(e);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          autoComplete="off"
                          sx={{
                            "& legend": { display: "none" },
                            "& fieldset": { top: 0 },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Vendors Section */}
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.terrain[50],
            borderRadius: 2,
            padding: 2,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, marginBottom: 2 }}
          >
            Select Vendors
          </Typography>
          {vendors?.map((vendor) => (
            <Box
              key={vendor.id}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: 1,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.terrain[100],
                },
              }}
              onClick={() => handleVendorToggle(vendor.id)}
            >
              <Checkbox
                checked={selectedVendorIds.includes(vendor.id)}
                sx={{
                  color: (theme) => theme.palette.primary[800],
                  "&.Mui-checked": {
                    color: (theme) => theme.palette.primary[800],
                  },
                }}
              />
              <Typography variant="body1">{vendor.name}</Typography>
            </Box>
          ))}
          {(!vendors || vendors.length === 0) && (
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.palette.terrain[500] }}
            >
              No vendors available
            </Typography>
          )}
        </Box>

        {/* Clear Filters Link */}
        {hasActiveFilters && (
          <Box sx={{ marginTop: 2, textAlign: "right" }}>
            <Button
              variant="text"
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.primary[800],
                  textDecoration: "underline",
                }}
              >
                Clear Filters
              </Typography>
            </Button>
          </Box>
        )}

        {/* Submit Button */}
        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            sx={{ height: "48px" }}
          >
            <Typography variant="largeButton">Set Filters</Typography>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default InvoiceFiltersModal;
